import { useMemo, useState } from "react";
import { Switch } from "@mui/material";
import { Check, Pencil, Trash2, X } from "lucide-react";

import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { WeekDayCircles } from "components/ui/WeekDayCircles";

import {
  formatStaffName,
  selectedDayIds,
  toDisplayDate,
  toTimeValue,
} from "../../utils/availabilityFormat";
import {
  SINGLE_DATE_HINT,
  toggleDay,
} from "../../utils/availabilityRecurrence";
import styles from "./StaffAvailabilitySection.module.css";
import { TimeField } from "./TimeField";

const ROWS_OPTIONS = [10, 20, 50, 100];

export const AvailabilityTable = ({
  availability,
  isLoading,
  isError,
  search,
  onSearchChange,
  onRetry,
  isSaving,
  onSave,
  onDelete,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [recordToDelete, setRecordToDelete] = useState(null);

  const startEditing = (record) => {
    setEditingId(record.id);
    setDraft({
      ...record,
      startTime: toTimeValue(record.startTime),
      endTime: toTimeValue(record.endTime),
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveDraft = async () => {
    const saved = await onSave(draft);
    if (saved) cancelEditing();
  };

  const updateDraft = (changes) =>
    setDraft((prev) => ({ ...prev, ...changes }));

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        width: "minmax(160px, 1fr)",
        render: (row) => formatStaffName(row.staffMember ?? row),
      },
      {
        id: "startTime",
        header: "Start Time",
        width: "110px",
        render: (row) =>
          editingId === row.id ? (
            <TimeField
              className={styles.cellTimeInput}
              value={draft.startTime ?? ""}
              onChange={(event) =>
                updateDraft({ startTime: event.target.value })
              }
              onClick={(event) => event.stopPropagation()}
            />
          ) : (
            toTimeValue(row.startTime) || "-"
          ),
      },
      {
        id: "endTime",
        header: "End Time",
        width: "110px",
        render: (row) =>
          editingId === row.id ? (
            <TimeField
              className={styles.cellTimeInput}
              value={draft.endTime ?? ""}
              onChange={(event) => updateDraft({ endTime: event.target.value })}
              onClick={(event) => event.stopPropagation()}
            />
          ) : (
            toTimeValue(row.endTime) || "-"
          ),
      },
      {
        id: "recurring",
        header: "Recurring",
        width: "110px",
        wrap: true,
        render: (row) => {
          if (editingId !== row.id) {
            return (
              <Pill
                {...(row.recurring
                  ? PILL_COLORS.statusSuccess
                  : PILL_COLORS.statusNoStatus)}
              >
                {row.recurring ? "Yes" : "No"}
              </Pill>
            );
          }

          return (
            <Switch
              size="small"
              checked={!!draft.recurring}
              onChange={(event) =>
                updateDraft({ recurring: event.target.checked })
              }
              onClick={(event) => event.stopPropagation()}
              inputProps={{ "aria-label": "Recurring" }}
            />
          );
        },
      },
      {
        id: "days",
        header: "Days",
        width: "230px",
        wrap: true,
        render: (row) => {
          const isEditing = editingId === row.id;
          const source = isEditing ? draft : row;

          return (
            <WeekDayCircles
              multiple
              value={selectedDayIds(source)}
              onChange={(day) => updateDraft(toggleDay(draft, day))}
              readOnly={!isEditing}
              disabled={isEditing && !draft.recurring}
              title={
                isEditing && !draft.recurring ? SINGLE_DATE_HINT : undefined
              }
            />
          );
        },
      },
      {
        id: "availableStartDate",
        header: "Start Date",
        width: "150px",
        wrap: true,
        render: (row) =>
          editingId === row.id ? (
            <DatePickerField
              value={draft.availableStartDate}
              onChange={(value) => updateDraft({ availableStartDate: value })}
            />
          ) : (
            toDisplayDate(row.availableStartDate)
          ),
      },
      {
        id: "availableEndDate",
        header: "End Date",
        width: "150px",
        wrap: true,
        render: (row) => {
          if (editingId !== row.id) {
            return toDisplayDate(row.availableEndDate);
          }

          // Kept visible while disabled: on a record saved under the old
          // semantics this date is still in effect until the row is re-saved.
          return (
            <DatePickerField
              value={draft.availableEndDate ?? ""}
              onChange={(value) => updateDraft({ availableEndDate: value })}
              disabled={!draft.recurring}
              placeholder={draft.recurring ? "Select date" : "Single date"}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingId, draft]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Save",
        icon: Check,
        primary: true,
        visibleWhen: (row) => editingId === row.id,
        disabled: isSaving,
        onClick: saveDraft,
      },
      {
        label: "Cancel",
        icon: X,
        primary: true,
        visibleWhen: (row) => editingId === row.id,
        onClick: cancelEditing,
      },
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        visibleWhen: (row) => editingId !== row.id,
        onClick: startEditing,
      },
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        visibleWhen: (row) => editingId !== row.id,
        onClick: (row) => setRecordToDelete(row),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingId, isSaving, draft]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={availability}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        isError={isError}
        errorLabel="staff availability"
        onRetry={onRetry}
        emptyTitle="No staff availability"
        emptyText={
          search
            ? "No staff member matches that name."
            : "Use Add new to give a staff member availability."
        }
        loadingTitle="Loading availability..."
        loadingText="Please wait while we fetch the roster."
        search={{
          value: search,
          onChange: onSearchChange,
          placeholder: "Search by staff name...",
        }}
        rowActions={rowActions}
        rowActionsLabel="Availability actions"
        rowsPerPage={20}
        rowsOptions={ROWS_OPTIONS}
      />

      <ConfirmDialog
        isOpen={!!recordToDelete}
        onClose={() => setRecordToDelete(null)}
        onConfirm={async () => {
          await onDelete(recordToDelete);
          setRecordToDelete(null);
        }}
        title="Delete staff availability"
        message="Are you sure you want to delete this availability? This action cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};
